/**
 * Unified calendar conversion facade: Solar Hijri (Shamsi), Gregorian (Miladi)
 * and Hijri (Qamari).
 *
 * All civil dates in this codebase are handled as "floating" calendar dates
 * (no time zone, no clock) to avoid off-by-one-day bugs. Instants are always
 * stored and transported as UTC ISO-8601 strings.
 */

import {
  gregorianMonthLength,
  gregorianToJdn,
  isGregorianLeapYear,
  isJalaliLeapYear,
  isValidGregorian,
  isValidJalali,
  jalaliMonthLength,
  jalaliToJdn,
  jdnToGregorian,
  jdnToJalali,
} from "./jalali.js";
import {
  detectHijriCalendar,
  hijriMonthLength,
  hijriToJdn,
  isHijriLeapYear,
  isValidHijri,
  jdnToHijri,
} from "./hijri.js";

export type CalendarSystem = "jalali" | "gregorian" | "hijri";

export interface CivilDate {
  year: number;
  month: number;
  day: number;
}

export const JALALI_MONTHS_FA = [
  "فروردین",
  "اردیبهشت",
  "خرداد",
  "تیر",
  "مرداد",
  "شهریور",
  "مهر",
  "آبان",
  "آذر",
  "دی",
  "بهمن",
  "اسفند",
] as const;

export const GREGORIAN_MONTHS_FA = [
  "ژانویه",
  "فوریه",
  "مارس",
  "آوریل",
  "مه",
  "ژوئن",
  "ژوئیه",
  "اوت",
  "سپتامبر",
  "اکتبر",
  "نوامبر",
  "دسامبر",
] as const;

export const HIJRI_MONTHS_FA = [
  "محرم",
  "صفر",
  "ربیع‌الاول",
  "ربیع‌الثانی",
  "جمادی‌الاول",
  "جمادی‌الثانی",
  "رجب",
  "شعبان",
  "رمضان",
  "شوال",
  "ذی‌القعده",
  "ذی‌الحجه",
] as const;

export const GREGORIAN_MONTHS_EN = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

export const HIJRI_MONTHS_EN = [
  "Muharram",
  "Safar",
  "Rabi al-Awwal",
  "Rabi al-Thani",
  "Jumada al-Ula",
  "Jumada al-Akhirah",
  "Rajab",
  "Shaban",
  "Ramadan",
  "Shawwal",
  "Dhu al-Qidah",
  "Dhu al-Hijjah",
] as const;

export const GREGORIAN_WEEKDAYS_FA = [
  "یکشنبه",
  "دوشنبه",
  "سه‌شنبه",
  "چهارشنبه",
  "پنجشنبه",
  "جمعه",
  "شنبه",
] as const;

export const GREGORIAN_WEEKDAYS_EN = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
] as const;

export const HIJRI_WEEKDAYS_FA = GREGORIAN_WEEKDAYS_FA;
export const HIJRI_WEEKDAYS_EN = GREGORIAN_WEEKDAYS_EN;

/** Weekday index 0=Sunday..6=Saturday for a Julian Day Number. */
export function jdnWeekday(jdn: number): number {
  return mod(jdn + 1, 7);
}

function mod(a: number, b: number): number {
  return a - Math.floor(a / b) * b;
}

// ---------------------------------------------------------------------------
// Cross-calendar conversion via Julian Day Numbers
// ---------------------------------------------------------------------------

/** Normalise the `{jy,jm,jd}` / `{gy,gm,gd}` / `{hy,hm,hd}` shapes to CivilDate. */
function fromJalali(v: { jy: number; jm: number; jd: number }): CivilDate {
  return { year: v.jy, month: v.jm, day: v.jd };
}
function fromGregorian(v: { gy: number; gm: number; gd: number }): CivilDate {
  return { year: v.gy, month: v.gm, day: v.gd };
}
function fromHijri(v: { hy: number; hm: number; hd: number }): CivilDate {
  return { year: v.hy, month: v.hm, day: v.hd };
}

export function gregorianToJalali(g: CivilDate): CivilDate {
  return fromJalali(jdnToJalali(gregorianToJdn(g.year, g.month, g.day)));
}

export function jalaliToGregorian(j: CivilDate): CivilDate {
  return fromGregorian(jdnToGregorian(jalaliToJdn(j.year, j.month, j.day)));
}

export function gregorianToHijri(
  g: CivilDate,
  calendar?: ReturnType<typeof detectHijriCalendar>,
): CivilDate {
  return fromHijri(
    jdnToHijri(gregorianToJdn(g.year, g.month, g.day), calendar),
  );
}

export function hijriToGregorian(
  h: CivilDate,
  calendar?: ReturnType<typeof detectHijriCalendar>,
): CivilDate {
  return fromGregorian(
    jdnToGregorian(hijriToJdn(h.year, h.month, h.day, calendar)),
  );
}

export function jalaliToHijri(
  j: CivilDate,
  calendar?: ReturnType<typeof detectHijriCalendar>,
): CivilDate {
  return fromHijri(
    jdnToHijri(jalaliToJdn(j.year, j.month, j.day), calendar),
  );
}

export function hijriToJalali(
  h: CivilDate,
  calendar?: ReturnType<typeof detectHijriCalendar>,
): CivilDate {
  return fromJalali(
    jdnToJalali(hijriToJdn(h.year, h.month, h.day, calendar)),
  );
}

// ---------------------------------------------------------------------------
// Date arithmetic
// ---------------------------------------------------------------------------

function jdnOf(
  d: CivilDate,
  system: CalendarSystem,
): number {
  switch (system) {
    case "jalali":
      return jalaliToJdn(d.year, d.month, d.day);
    case "hijri":
      return hijriToJdn(d.year, d.month, d.day);
    case "gregorian":
      return gregorianToJdn(d.year, d.month, d.day);
  }
}

function fromJdn(
  jdn: number,
  system: CalendarSystem,
): CivilDate {
  switch (system) {
    case "jalali":
      return fromJalali(jdnToJalali(jdn));
    case "hijri":
      return fromHijri(jdnToHijri(jdn));
    case "gregorian":
      return fromGregorian(jdnToGregorian(jdn));
  }
}

/** Add (or subtract) a number of days, staying in the same calendar system. */
export function addDays(
  d: CivilDate,
  days: number,
  system: CalendarSystem,
): CivilDate {
  return fromJdn(jdnOf(d, system) + days, system);
}

/** Whole days from `a` to `b` (positive when `b` is later). */
export function diffDays(
  a: CivilDate,
  b: CivilDate,
  system: CalendarSystem,
): number {
  return jdnOf(b, system) - jdnOf(a, system);
}

export function startOfMonth(
  d: CivilDate,
  // Day 1 is day 1 in all three calendars, so the system is not needed. The
  // parameter stays for symmetry with daysInMonth().
  _system: CalendarSystem,
): CivilDate {
  return { ...d, day: 1 };
}

export function daysInMonth(
  d: Pick<CivilDate, "year" | "month">,
  system: CalendarSystem,
): number {
  switch (system) {
    case "jalali":
      return jalaliMonthLength(d.year, d.month);
    case "hijri":
      return hijriMonthLength(d.year, d.month);
    case "gregorian":
      return gregorianMonthLength(d.year, d.month);
  }
}

export function isLeapYear(
  year: number,
  system: CalendarSystem,
): boolean {
  switch (system) {
    case "jalali":
      return isJalaliLeapYear(year);
    case "hijri":
      return isHijriLeapYear(year);
    case "gregorian":
      return isGregorianLeapYear(year);
  }
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

export function isValidDate(
  d: CivilDate,
  system: CalendarSystem,
): boolean {
  switch (system) {
    case "jalali":
      return isValidJalali(d.year, d.month, d.day);
    case "hijri":
      return isValidHijri(d.year, d.month, d.day);
    case "gregorian":
      return isValidGregorian(d.year, d.month, d.day);
  }
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

/** Narrow a tuple/array lookup to a non-optional element type. */
function pick<T>(arr: readonly T[], index: number): T {
  const v = arr[index];
  if (v === undefined) throw new RangeError(`Index ${index} out of range`);
  return v;
}

export function monthName(
  month: number,
  system: CalendarSystem,
  lang: "fa" | "en" = "fa",
): string {
  if (lang === "en") {
    if (system === "gregorian") return pick(GREGORIAN_MONTHS_EN, month - 1);
    if (system === "hijri") return pick(HIJRI_MONTHS_EN, month - 1);
    return pick(JALALI_MONTHS_EN, month - 1);
  }
  if (system === "gregorian") return pick(GREGORIAN_MONTHS_FA, month - 1);
  if (system === "hijri") return pick(HIJRI_MONTHS_FA, month - 1);
  return pick(JALALI_MONTHS_FA, month - 1);
}

const JALALI_MONTHS_EN = [
  "Farvardin",
  "Ordibehesht",
  "Khordad",
  "Tir",
  "Mordad",
  "Shahrivar",
  "Mehr",
  "Aban",
  "Azar",
  "Dey",
  "Bahman",
  "Esfand",
] as const;

/** Latin transliteration of Solar Hijri digits: 1405 -> "1405", year "۱۴۰۵" -> "1405". */
const FA_DIGITS = "۰۱۲۳۴۵۶۷۸۹";
const FA_DIGITS_ALT = "٠١٢٣٤٥٦٧٨٩";

export function toLatinDigits(input: string): string {
  return input.replace(/[۰-۹٠-٩]/g, (c) => {
    const faIdx = FA_DIGITS.indexOf(c);
    if (faIdx >= 0) return String(faIdx);
    return String(FA_DIGITS_ALT.indexOf(c));
  });
}

export function toPersianDigits(input: string | number): string {
  return String(input).replace(/[0-9]/g, (d) => FA_DIGITS.charAt(Number(d)));
}

export interface FormatOptions {
  lang?: "fa" | "en";
  /** "long" -> 7 مهر 1405 | "short" -> 1405/07/07 | "iso" -> 1405-07-07 */
  style?: "long" | "short" | "iso";
  withWeekday?: boolean;
}

export function formatDate(
  d: CivilDate,
  system: CalendarSystem,
  opts: FormatOptions = {},
): string {
  const { lang = "fa", style = "long", withWeekday = false } = opts;
  const pad = (n: number) => String(n).padStart(2, "0");
  let out: string;
  if (style === "iso") {
    out = `${d.year}-${pad(d.month)}-${pad(d.day)}`;
  } else if (style === "short") {
    out = `${d.year}/${pad(d.month)}/${pad(d.day)}`;
  } else {
    out = `${d.day} ${monthName(d.month, system, lang)} ${d.year}`;
  }
  if (withWeekday) {
    const wd = weekdayName(d, system, lang);
    out = lang === "fa" ? `${wd}، ${out}` : `${out}, ${wd}`;
  }
  return lang === "fa" ? toPersianDigits(out) : toLatinDigits(out);
}

export function weekdayName(
  d: CivilDate,
  system: CalendarSystem,
  lang: "fa" | "en" = "fa",
): string {
  const idx = jdnWeekday(jdnOf(d, system));
  return lang === "en"
    ? pick(GREGORIAN_WEEKDAYS_EN, idx)
    : pick(GREGORIAN_WEEKDAYS_FA, idx);
}

export function weekdayIndex(d: CivilDate, system: CalendarSystem): number {
  return jdnWeekday(jdnOf(d, system));
}

/**
 * Today's civil date in the given calendar, for a given IANA time zone.
 * Pass `at` to evaluate a specific instant instead of "now" - the Telegram bot
 * and the tests need that for deterministic results.
 */
export function today(
  system: CalendarSystem,
  timeZone = "UTC",
  at: Date | string = new Date(),
): CivilDate {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "numeric",
    day: "numeric",
  }).formatToParts(typeof at === "string" ? new Date(at) : at);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value);
  return fromJdn(gregorianToJdn(get("year"), get("month"), get("day")), system);
}

/** The JDN (astronomical day number) of a civil date - the universal day key. */
export function toJdn(
  d: CivilDate,
  system: CalendarSystem,
): number {
  return jdnOf(d, system);
}

export function fromJdnPublic(
  jdn: number,
  system: CalendarSystem,
): CivilDate {
  return fromJdn(jdn, system);
}

/** All three representations of one physical day. */
export function describeDay(
  anchor: CivilDate,
  system: CalendarSystem,
): Record<CalendarSystem, CivilDate> {
  const jdn = jdnOf(anchor, system);
  return {
    jalali: fromJalali(jdnToJalali(jdn)),
    gregorian: fromGregorian(jdnToGregorian(jdn)),
    hijri: fromHijri(jdnToHijri(jdn)),
  };
}

// ---------------------------------------------------------------------------
// Instant <-> civil date bridging (time-zone aware)
// ---------------------------------------------------------------------------

/**
 * Convert a UTC instant to the civil date/time actually shown to a user in
 * `timeZone`. Uses the host's IANA database so DST is handled correctly.
 */
export function instantToCivil(
  iso: string,
  timeZone: string,
  system: CalendarSystem,
): { date: CivilDate; hour: number; minute: number } {
  const d = new Date(iso);
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const parts = fmt.formatToParts(d);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value);
  const gy = get("year");
  const gmo = get("month");
  const gd = get("day");
  const hour = get("hour") === 24 ? 0 : get("hour");
  return {
    date: fromJdn(gregorianToJdn(gy, gmo, gd), system),
    hour,
    minute: get("minute"),
  };
}

/**
 * Inverse of {@link instantToCivil}: build the UTC instant for a wall-clock
 * time in `timeZone`.
 *
 * A local wall time is not always a single instant. During a DST spring-forward
 * gap it does not exist at all, and during a fall-back overlap it happens
 * twice. We gather the candidate offsets in effect around the target, keep the
 * ones that round-trip exactly, and otherwise fall back to the earliest
 * instant whose local time is at or after the target - which pushes a gap
 * forward by exactly the size of the transition and resolves an overlap to its
 * first occurrence.
 */
export function civilToInstant(
  date: CivilDate,
  hour: number,
  minute: number,
  timeZone: string,
  system: CalendarSystem,
): string {
  const g = system === "gregorian" ? date : civilToGregorian(date, system);
  const targetWallMs = Date.UTC(g.year, g.month - 1, g.day, hour, minute);
  const DAY_MS = 86400000;

  // Probe well before and well after the target so that both sides of any
  // nearby transition are represented.
  const probes = [
    targetWallMs - 2 * DAY_MS,
    targetWallMs - DAY_MS,
    targetWallMs,
    targetWallMs + DAY_MS,
    targetWallMs + 2 * DAY_MS,
  ];
  const candidates = new Set<number>();
  for (const p of probes) {
    candidates.add(targetWallMs - timeZoneOffsetMs(new Date(p), timeZone));
  }

  // An unambiguous wall time round-trips exactly; take it immediately.
  for (const c of [...candidates].sort((a, b) => a - b)) {
    if (localWallMsAt(c, timeZone) === targetWallMs) {
      return new Date(c).toISOString();
    }
  }

  // Otherwise: the first instant at or after which the local clock reads the
  // target or later.
  const forward = [...candidates]
    .filter((c) => localWallMsAt(c, timeZone) >= targetWallMs)
    .sort((a, b) => a - b)[0];

  if (forward !== undefined) return new Date(forward).toISOString();
  // Pathological zone with no forward-resolvable instant; use the latest
  // candidate so the result stays inside the requested day.
  return new Date(Math.max(...candidates)).toISOString();
}

/** The wall-clock time (as if UTC) that `timeZone` shows at this instant. */
function localWallMsAt(instantMs: number, timeZone: string): number {
  return instantMs + timeZoneOffsetMs(new Date(instantMs), timeZone);
}

function civilToGregorian(
  d: CivilDate,
  system: CalendarSystem,
): CivilDate {
  if (system === "gregorian") return d;
  return fromJdn(jdnOf(d, system), "gregorian");
}

/** Offset of `timeZone` from UTC at instant `at`, in milliseconds. */
export function timeZoneOffsetMs(at: Date, timeZone: string): number {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const parts = dtf.formatToParts(at);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value);
  const asUtc = Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    get("hour") % 24,
    get("minute"),
    get("second"),
  );
  return asUtc - Math.floor(at.getTime() / 1000) * 1000;
}

export { detectHijriCalendar, isHijriLeapYear, hijriMonthLength };
